import {
  type FC,
  type ChangeEvent,
  type KeyboardEvent,
  useRef,
  useState,
  useEffect,
  useCallback,
  Fragment,
} from 'react'
import cx from 'classnames'
import { type DefaultProps, Spinner } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { CheckIcon, TrashIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { type FileUploadSuccess, type FileUploadError } from '@egw/api'
import { type UseMutateFunction } from '@tanstack/react-query'
import { type HttpError } from '@egw/clients'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ACCEPTED_FILE_TYPES = '.json,.pdf,.jpeg,.jpg,.png,.csv,.txt'

// eslint-disable-next-line no-unused-vars
enum Status {
  // eslint-disable-next-line no-unused-vars
  UPLOADING = 'uploading',
  // eslint-disable-next-line no-unused-vars
  UPLOADED = 'uploaded',
  // eslint-disable-next-line no-unused-vars
  FAILED = 'failed',
}

export interface UploadedFile {
  filePath: string
  name: string
  size: number
}

interface FileComponentProps {
  file: File
  mutate: UseMutateFunction<FileUploadSuccess | FileUploadError, HttpError, File>
  onUploaded: (_filePath: string) => void
  onDelete: () => void
  isDisabled: boolean
}

const FileComponent: FC<FileComponentProps> = ({ file, mutate, onUploaded, onDelete, isDisabled = false }) => {
  const [status, setStatus] = useState<Status>(Status.UPLOADING)
  const [maxFileSizeError, setMaxFileSizeError] = useState<string | null>(null)

  const { t } = useTranslation('features-system')

  const uploadFile = useCallback((): void => {
    if (status !== Status.UPLOADING) {
      setStatus(Status.UPLOADING)
    }
    mutate(file, {
      onSuccess: (res) => {
        if ((res as FileUploadSuccess).success) {
          setStatus(Status.UPLOADED)
          onUploaded((res as FileUploadSuccess).filePath)
        } else {
          setStatus(Status.FAILED)
        }
      },
      onError: () => {
        setStatus(Status.FAILED)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (file.size <= MAX_FILE_SIZE) {
      uploadFile()
    } else {
      setStatus(Status.FAILED)
      setMaxFileSizeError(t('file_upload.upload_failed_max_size'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div key={file.name} className="flex items-center gap-xs">
      {status === Status.UPLOADED && <CheckIcon className="w-5 h-5 text-icons-full" />}
      {status === Status.UPLOADING && <Spinner className="w-5 h-5 text-gray-300" />}
      {status === Status.FAILED && <ExclamationTriangleIcon className="w-5 h-5 text-icons-error" />}
      <div className="flex-1 relative flex items-center">
        <div className="w-full absolute">
          <p className="typo-primary-base-regular truncate">{file.name}</p>
          {status === Status.FAILED && (
            <p className="typo-primary-sm-regular text-text-error truncate">
              {maxFileSizeError ?? t('file_upload.upload_failed')}
            </p>
          )}
        </div>
      </div>
      {status === Status.FAILED && !maxFileSizeError && (
        <button
          type="button"
          className={cx({
            'cursor-pointer': !isDisabled,
            'opacity-50 cursor-not-allowed': isDisabled,
          })}
          onClick={() => {
            uploadFile()
          }}
          disabled={isDisabled}
        >
          <ArrowPathIcon className="w-5 h-5 text-icons-action mr-xs" />
        </button>
      )}
      {(status === Status.UPLOADED || status === Status.FAILED) && (
        <button
          type="button"
          className={cx({
            'cursor-pointer': !isDisabled,
            'opacity-50 cursor-not-allowed': isDisabled,
          })}
          onClick={() => {
            onDelete()
          }}
          disabled={isDisabled}
        >
          <TrashIcon className="w-5 h-5 text-icons-full" />
        </button>
      )}
      {status === Status.UPLOADING && (
        <button
          type="button"
          className={cx({
            'cursor-pointer': !isDisabled,
            'opacity-50 cursor-not-allowed': isDisabled,
          })}
          onClick={() => {
            onDelete()
          }}
          disabled={isDisabled}
        >
          <XMarkIcon className="w-6 h-6 text-icons-full" />
        </button>
      )}
    </div>
  )
}

interface FileUploadProps extends DefaultProps {
  /**
   * Callback when files are selected
   */
  onChange: (_files: UploadedFile[]) => void
  /**
   * Mutate function
   */
  mutate: UseMutateFunction<FileUploadSuccess | FileUploadError, HttpError, File>
  /**
   * Upload button title
   */
  buttonLabel?: string
  /**
   * Upload button title
   */
  listTitle?: string
  /**
   * Whether the component is disabled
   */
  isDisabled?: boolean
}

export const FileUpload: FC<FileUploadProps> = ({
  onChange,
  mutate,
  buttonLabel,
  listTitle,
  isDisabled = false,
  id,
  testId,
  ariaLabel,
}) => {
  const { t } = useTranslation('features-system')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  useEffect(() => {
    onChange(uploadedFiles)
  }, [uploadedFiles, onChange])

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      handleFiles(files)
    }
  }

  const handleFiles = (files: File[]): void => {
    const validFiles = files.filter((file) => {
      const acceptedTypes = ACCEPTED_FILE_TYPES.split(',').map((type) => type.trim())
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`
      if (!acceptedTypes.some((type) => type.toLowerCase() === fileExtension)) {
        return false
      }
      return true
    })

    // Filter out files that already exist in selectedFiles
    const uniqueFiles = validFiles.filter(
      (file) =>
        !selectedFiles.some((existingFile) => existingFile.name === file.name && existingFile.size === file.size),
    )

    // Only update state if the files list has actually changed
    const newSelectedFiles = [...selectedFiles, ...uniqueFiles]
    const currentFilesKey = selectedFiles.map((f) => `${f.name}-${f.size.toString()}`).join(',')
    const newFilesKey = newSelectedFiles.map((f) => `${f.name}-${f.size.toString()}`).join(',')

    if (currentFilesKey !== newFilesKey) {
      setSelectedFiles(newSelectedFiles)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      fileInputRef.current?.click()
    }
  }

  const handleClick = (): void => {
    if (!isDisabled) {
      fileInputRef.current?.click()
    }
  }

  const buttonClasses = cx(
    'px-sm py-2xs typo-primary-sm-semibold border rounded-lg',
    'text-btnSecondaryText disabled:text-btnSecondaryTextDisabled',
    'bg-btnSecondaryBg disabled:bg-btnSecondaryBgDisabled',
    'border-btnSecondaryBorder disabled:border-btnSecondaryBorderDisabled',
    {
      'active:bg-btnSecondaryBgActive hover:bg-btnSecondaryBgHover': !isDisabled,
      'shadow-sm hover:shadow-md active:shadow-none': !isDisabled,
      'active:text-btnSecondaryTextActive hover:text-btnSecondaryTextHover': !isDisabled,
      'active:border-btnSecondaryBorderActive hover:border-btnSecondaryBorderHover': !isDisabled,
      'opacity-75 cursor-not-allowed': isDisabled,
    },
  )

  return (
    <div
      className="border border-gray-200 rounded-xl py-lg px-md"
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {selectedFiles.length > 0 && (
        <div className="pb-xl">
          <p className="typo-primary-md-light mb-xl">{listTitle ?? t('file_upload.upload_your_statements')}</p>
          <div className="flex flex-col gap-md">
            {selectedFiles.map((file, index) => (
              <Fragment key={file.name}>
                {index > 0 && <hr className="border-gray-200" />}
                <FileComponent
                  file={file}
                  mutate={mutate}
                  onUploaded={(filePath: string) => {
                    if (!uploadedFiles.some((uploadedFile) => uploadedFile.filePath === filePath)) {
                      setUploadedFiles([...uploadedFiles, { filePath, name: file.name, size: file.size }])
                    }
                  }}
                  onDelete={() => {
                    setSelectedFiles(selectedFiles.filter((f) => f.name !== file.name))
                    setUploadedFiles(uploadedFiles.filter((f) => f.name !== file.name))
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                  isDisabled={isDisabled}
                />
              </Fragment>
            ))}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        multiple={false}
        onChange={handleFileInput}
        className="hidden"
        disabled={isDisabled}
      />

      <button
        type="button"
        className={buttonClasses}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        tabIndex={0}
        disabled={isDisabled}
      >
        {buttonLabel ?? t('file_upload.upload_statement')}
      </button>
    </div>
  )
}
